/* eslint-disable */
import OrderAPI from '@/apps/order/classes/api/OrderAPI';
import { OrderTypeSettingsCategoryKey } from '@/interfaces/OrderAppSettingsOrderTypesInterface';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { autoUpdateSessionStorage } from '@/apps/order/helper';

import { useRouterStore } from '@/apps/order/stores';

import DataPreparedAddressInterface from '@/apps/order/interfaces/ordersCreate/DataPreparedAddressInterface';
import DataPreparedConsumptionPointInterface, {
  SingleSettingsInterface
} from '@/apps/order/interfaces/ordersCreate/DataPreparedConsumptionPointInterface';
import DataPreparedPaymentInterface from '@/apps/order/interfaces/ordersCreate/DataPreparedPaymentInterface';
import CompanySettingInterface from '@/apps/order/interfaces/CompanySettingInterface';
import { DataPreparedCustomFieldsInterface } from '@/apps/order/interfaces/ordersCreate/DataPreparedCustomFieldsInterface';
import DataPreparedIntentInterface from '@/apps/order/interfaces/ordersCreate/DataPreparedIntentInterface';
import type { ContractSummaryState } from '@/apps/order/helper/contractSummary';

const useOrdersCreateDataPreparedStore = defineStore('ordersCreateDataPrepared', () => {
  const routerStore = useRouterStore();
  const { meta } = storeToRefs(routerStore);

  const dataAddress = ref<DataPreparedAddressInterface | undefined>();
  autoUpdateSessionStorage(`${meta.value.ctx}.preparedDataAddress`, () => dataAddress.value, 1000);

  const dataConsumptionPoint = ref<DataPreparedConsumptionPointInterface | undefined>();

  const getDataConsumptionPointByCategoryKey = (categoryKey: string): SingleSettingsInterface | undefined => {
    const key = categoryKey as OrderTypeSettingsCategoryKey;

    return (dataConsumptionPoint.value?.isSeparateOrderTypes ? {
      changeDates: dataConsumptionPoint.value.changeDates[key]
        ?? dataConsumptionPoint.value.changeDates.default,
      orderTypes: dataConsumptionPoint.value.orderTypes[key]
        ?? dataConsumptionPoint.value.orderTypes.default,
      orderTypesSettings: dataConsumptionPoint.value.orderTypesSettings[key]
        ?? dataConsumptionPoint.value.orderTypesSettings.default,
    } : dataConsumptionPoint.value) as SingleSettingsInterface;
  };

  const dataPayment = ref<DataPreparedPaymentInterface | undefined>();

  const getProductChangeDateOptions = (categoryKey: string): {
    value: string,
    label: string,
  }[] => (getDataConsumptionPointByCategoryKey(categoryKey)?.changeDates.productChange.dates ?? []).map((dateString) => ({
    value: dateString,
    label: new Date(dateString).format('d.m.Y') as string,
  }))

  const getProductChangeDatePickerDates = (categoryKey: string): {
    min: Date | null,
    max: Date | null,
    dates: string[],
  } => {
    const { min, max, dates } = getDataConsumptionPointByCategoryKey(categoryKey)?.changeDates.productChange ?? {};

    return {
      min: min ? new Date(min as string) : null,
      max: max ? new Date(max as string) : null,
      dates: dates as string[],
    };
  };

  const getPreviousSupplierChangeDateOptions = (categoryKey: string): {
    value: string,
    label: string,
  }[] => (getDataConsumptionPointByCategoryKey(categoryKey)?.changeDates.previousSupplierChange.dates ?? []).map((dateString) => ({
    value: dateString,
    label: new Date(dateString).format('d.m.Y') as string,
  }))

  const getPreviousSupplierChangePickerDates = (categoryKey: string): {
    min: Date | null,
    max: Date | null,
    dates: string[],
  } => {
    const { min, max, dates } = getDataConsumptionPointByCategoryKey(categoryKey)?.changeDates.previousSupplierChange ?? {};

    return {
      min: min ? new Date(min as string) : null,
      max: max ? new Date(max as string) : null,
      dates: dates as string[],
    };
  };

  const getMovingDatePickerDates = (categoryKey: string): {
    min: Date | null,
    max: Date | null,
    dates: string[],
  } => {
    const { min, max, dates } = getDataConsumptionPointByCategoryKey(categoryKey)?.changeDates.movingChange ?? {};

    return {
      min: min ? new Date(min as string) : null,
      max: max ? new Date(max as string) : null,
      dates: dates as string[],
    };
  };

  const companySettings = ref<CompanySettingInterface>();
  const fetchCompanySetting = (): Promise<CompanySettingInterface> => new Promise<CompanySettingInterface>((resolve, reject) => {
    OrderAPI.fetchCompanySettings()
      .then((fetchedDataPrepared) => {
        companySettings.value = fetchedDataPrepared;
        resolve(fetchedDataPrepared);
      })
      .catch(e => {
        reject(e)
      });
  });

  const customFields = ref<DataPreparedCustomFieldsInterface | undefined>();
  autoUpdateSessionStorage(`${meta.value.ctx}.customFields`, () => customFields.value, 1000);

  const intentTexts = ref<Record<string, DataPreparedIntentInterface>>({});

  const intentTypeActive = (type: string, category: string): boolean => (
    ![undefined, null, ''].includes((intentTexts.value?.[category] as Record<string, any> | undefined)?.[type])
  );

  // Written by OrderCreateOverviewIntent (PDF generation), read by the submit gate in OrderCreateOverviewView.
  const contractSummaryStates = ref<Record<string, ContractSummaryState>>({});

  const setContractSummaryState = (category: string, state: ContractSummaryState): void => {
    contractSummaryStates.value = { ...contractSummaryStates.value, [category]: state };
  };

  const isContractSummaryRequired = (category: string): boolean => (
    contractSummaryStates.value[category] !== 'unavailable'
  );

  return {
    dataAddress,
    dataConsumptionPoint,
    getDataConsumptionPointByCategoryKey,
    dataPayment,
    getMovingDatePickerDates,
    getProductChangeDateOptions,
    getProductChangeDatePickerDates,
    getPreviousSupplierChangeDateOptions,
    getPreviousSupplierChangePickerDates,
    companySettings,
    customFields,
    fetchCompanySetting,
    intentTexts,
    intentTypeActive,
    contractSummaryStates,
    setContractSummaryState,
    isContractSummaryRequired,
  };
});

export default useOrdersCreateDataPreparedStore;
